/**
 * Filter system utilities for EventSearch
 * Provides data model, factory functions, and query building logic
 */

export {
  FILTER_OPERATORS,
  QUEUE_MODES,
  generateFilterId,
} from '@/utils/selection'

import { FILTER_OPERATORS, QUEUE_MODES, generateFilterId } from '@/utils/selection'

// Filter types
export const FILTER_TYPES = {
  FIELD_VALUE: 'field-value',   // field:value (e.g., ip_src:1.1.1.1)
  VALUE_ONLY: 'value-only',     // just value in any field
  TIME_RANGE: 'time-range',     // @timestamp:[start TO end] (event.ts / event time)
  INGEST_SINCE: 'ingest-since', // @ingested:[start TO *] ("new since last viewed")
  COMPOUND: 'compound'          // multiple field:value pairs in one filter
}

/**
 * Create a new filter object
 * @param {string} type - FILTER_TYPES value
 * @param {string|null} field - field name (null for value-only or time)
 * @param {Array|Object} values - array of values or {start, end} for time
 * @param {string|null} operator - FILTER_OPERATORS value (null for time)
 * @returns {Object} filter object
 */
export function createFilter(type, field, values, operator) {
  return {
    id: generateFilterId(),
    type,
    field,
    values,
    operator,
    enabled: true,
    createdAt: Date.now()
  }
}

/**
 * Create a time range filter
 * @param {number} startMs - start time in milliseconds
 * @param {number|null} endMs - end time in milliseconds (null for open-ended)
 * @returns {Object} time filter object
 */
export function createTimeFilter(startMs, endMs = null) {
  return createFilter(
    FILTER_TYPES.TIME_RANGE,
    null,
    { start: startMs, end: endMs },
    null
  )
}

/**
 * Create an "ingested since" filter — an open-ended lower bound on the ingestion
 * axis (@ingested), used to show results newly ingested since the user's
 * watermark. Distinct from the event-time TIME_RANGE filter so the two coexist.
 * @param {number} startMs - watermark in milliseconds (epoch)
 * @returns {Object} ingest-since filter object
 */
export function createIngestSinceFilter(startMs) {
  return createFilter(
    FILTER_TYPES.INGEST_SINCE,
    null,
    { start: startMs },
    null
  )
}

/**
 * Format a value for use in a Lucene query
 * Always quotes values to ensure exact matching
 * @param {any} value - value to format
 * @returns {string} formatted value
 */
function formatQueryValue(value) {
  if (value === null || value === undefined) {
    return '*'
  }

  const strValue = String(value)
  // Always quote to ensure exact matching
  return `"${strValue.replace(/"/g, '\\"')}"`
}

/**
 * Does a Lucene expression contain a boolean OR at the top level (paren-depth 0,
 * outside quotes)? Such an expression must be parenthesized before it can be
 * AND-combined, since OR binds looser than AND. Only uppercase `OR` / `||` count
 * as operators in Lucene's default parser — lowercase `or` is a search term.
 * @param {string} str - Lucene expression
 * @returns {boolean}
 */
export function hasTopLevelOr(str) {
  let depth = 0
  let inQuote = false
  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (inQuote) {
      if (c === '\\') { i++; continue }
      if (c === '"') inQuote = false
      continue
    }
    if (c === '"') { inQuote = true; continue }
    else if (c === '(') depth++
    else if (c === ')') depth--
    else if (depth === 0) {
      if (c === '|' && str[i + 1] === '|') return true
      if (c === 'O' && str[i + 1] === 'R') {
        const before = i === 0 || /\s|\(/.test(str[i - 1])
        const after = i + 2 >= str.length || /\s|\)/.test(str[i + 2])
        if (before && after) return true
      }
    }
  }
  return false
}

/**
 * AND-combine two Lucene expressions, parenthesizing an operand only when it has
 * a top-level OR (which would otherwise rebind under AND). AND is associative, so
 * a chain of appends stays flat — `a AND b AND c`, not `((a) AND b) AND c` — and
 * only genuine OR groups get wrapped: `(x OR y) AND a AND b`.
 * @param {string} left
 * @param {string} right
 * @returns {string}
 */
export function andJoin(left, right) {
  const l = left.trim()
  const r = right.trim()
  if (!l) return r
  if (!r) return l
  const lw = hasTopLevelOr(l) ? `(${l})` : l
  const rw = hasTopLevelOr(r) ? `(${r})` : r
  return `${lw} AND ${rw}`
}

/**
 * Convert a single filter to Lucene query syntax
 * @param {Object} filter - filter object
 * @returns {string} Lucene query string
 */
export function filterToQueryString(filter) {
  if (!filter.enabled) {
    return ''
  }

  switch (filter.type) {
    case FILTER_TYPES.TIME_RANGE: {
      const { start, end } = filter.values
      if (end === null || end === undefined) {
        // Open-ended time filter (no end bound)
        return `@timestamp:[${start} TO *]`
      }
      return `@timestamp:[${start} TO ${end}]`
    }

    case FILTER_TYPES.INGEST_SINCE: {
      // Open-ended lower bound on ingestion time (matches how "new results" are
      // counted), never the event-time axis. @ingested is the API alias for the
      // event.ingested date field, which accepts epoch ms.
      const { start } = filter.values
      return `@ingested:[${start} TO *]`
    }

    case FILTER_TYPES.FIELD_VALUE: {
      const values = Array.isArray(filter.values) ? filter.values : [filter.values]
      const field = filter.field

      if (values.length === 0) return ''

      // Build value expressions
      const valueExprs = values.map(v => `${field}:${formatQueryValue(v)}`)

      let expr
      if (values.length === 1) {
        expr = valueExprs[0]
      } else {
        const joiner = filter.operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
        expr = `(${valueExprs.join(joiner)})`
      }

      if (filter.operator === FILTER_OPERATORS.NOT) {
        return `NOT ${expr}`
      }
      return expr
    }

    case FILTER_TYPES.VALUE_ONLY: {
      const values = Array.isArray(filter.values) ? filter.values : [filter.values]

      if (values.length === 0) return ''

      // Build value expressions (no field prefix)
      const valueExprs = values.map(v => formatQueryValue(v))

      let expr
      if (values.length === 1) {
        expr = valueExprs[0]
      } else {
        const joiner = filter.operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
        expr = `(${valueExprs.join(joiner)})`
      }

      if (filter.operator === FILTER_OPERATORS.NOT) {
        return `NOT ${expr}`
      }
      return expr
    }

    case FILTER_TYPES.COMPOUND: {
      const pairs = filter.values // Array of { field, value }
      if (!pairs || pairs.length === 0) return ''

      // Build expressions for each field:value pair
      const exprs = pairs.map(pair => {
        if (pair.empty) return `(NOT ${pair.field}:*)`
        const formattedValue = formatQueryValue(pair.value)
        return pair.field
          ? `${pair.field}:${formattedValue}`
          : formattedValue
      })

      let expr
      if (exprs.length === 1) {
        expr = exprs[0]
      } else {
        const joiner = filter.operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
        expr = `(${exprs.join(joiner)})`
      }

      if (filter.operator === FILTER_OPERATORS.NOT) {
        return `NOT ${expr}`
      }
      return expr
    }

    default:
      return ''
  }
}

/**
 * Combine all enabled filters into a single query string
 * @param {Array} filters - array of filter objects
 * @returns {string} combined Lucene query string
 */
export function filtersToQueryString(filters) {
  const parts = filters
    .filter(f => f.enabled)
    .map(f => filterToQueryString(f))
    .filter(s => s.length > 0)

  if (parts.length === 0) {
    return ''
  }

  if (parts.length === 1) {
    return parts[0]
  }

  // Combine with AND (each filter is a separate constraint), wrapping only the
  // parts that carry a top-level OR.
  return parts.reduce((acc, p) => andJoin(acc, p))
}

/**
 * Build the final query combining base query and filters
 * @param {string} baseQuery - user's base query
 * @param {Array} filters - array of filter objects
 * @returns {string} final query string
 */
export function buildFinalQuery(baseQuery, filters) {
  const filterQuery = filtersToQueryString(filters)
  const trimmedBase = baseQuery.trim()

  if (!trimmedBase && !filterQuery) {
    return ''
  }

  if (!trimmedBase) {
    return filterQuery
  }

  if (!filterQuery) {
    return trimmedBase
  }

  return andJoin(trimmedBase, filterQuery)
}

/**
 * Find an existing time filter in the filters array
 * @param {Array} filters - array of filter objects
 * @returns {Object|null} time filter or null
 */
export function findTimeFilter(filters) {
  return filters.find(f => f.type === FILTER_TYPES.TIME_RANGE) || null
}

/**
 * Find an existing "ingested since" filter in the filters array
 * @param {Array} filters - array of filter objects
 * @returns {Object|null} ingest-since filter or null
 */
export function findIngestFilter(filters) {
  return filters.find(f => f.type === FILTER_TYPES.INGEST_SINCE) || null
}

/**
 * Group queue items by field for filter creation
 * @param {Array} queueItems - array of queue items
 * @returns {Object} { fieldValue: { field -> items }, valueOnly: items }
 */
export function groupQueueItems(queueItems) {
  const fieldValue = {}
  const valueOnly = []

  for (const item of queueItems) {
    if (item.mode === QUEUE_MODES.FIELD_VALUE) {
      if (!fieldValue[item.field]) {
        fieldValue[item.field] = []
      }
      fieldValue[item.field].push(item)
    } else {
      valueOnly.push(item)
    }
  }

  return { fieldValue, valueOnly }
}

/**
 * Create a single compound filter from grouped queue items
 * All selections are combined into one filter with multiple field:value pairs
 * @param {Object} grouped - result from groupQueueItems
 * @param {string} operator - FILTER_OPERATORS value
 * @returns {Array} array containing single compound filter
 */
export function createFiltersFromQueue(grouped, operator) {
  // Collect all field-value pairs into a single array
  const pairs = []

  // Add field-value pairs
  for (const [field, items] of Object.entries(grouped.fieldValue)) {
    items.forEach(item => {
      pairs.push({ field, value: item.rawValue, empty: item.empty })
    })
  }

  // Add value-only pairs (field is null)
  grouped.valueOnly.forEach(item => {
    pairs.push({ field: null, value: item.rawValue })
  })

  if (pairs.length === 0) return []

  // Create single compound filter containing all pairs
  return [createFilter(
    FILTER_TYPES.COMPOUND,
    null,
    pairs,  // Array of { field, value } objects
    operator
  )]
}

/**
 * Build the Lucene clause for a set of selection items under an operator (the
 * string a table's +/- click contributes to the query). Empty when the items
 * produce nothing. Shared by Event Search and the selector filter bar so both
 * emit an identical clause format.
 * @param {Array} queueItems - selection items (from makeSelectionItem)
 * @param {string} operator - FILTER_OPERATORS value
 * @returns {string}
 */
export function buildFilterClause(queueItems, operator) {
  const grouped = groupQueueItems(queueItems)
  return createFiltersFromQueue(grouped, operator)
    .map(f => filterToQueryString(f))
    .filter(s => s.length > 0)
    .join(' AND ')
}

/**
 * Remove a previously-appended clause from a query string and tidy up any
 * dangling operators / empty groups left behind. Used to toggle a +/- filter
 * back off without leaving `field:"x" AND` fragments.
 * @param {string} q - the current query string
 * @param {string} clause - the exact clause previously appended
 * @returns {string}
 */
export function stripClauseFromQuery(q, clause) {
  let s = q
  const candidates = [` AND ${clause}`, `${clause} AND `, clause]
  for (const c of candidates) {
    const idx = s.indexOf(c)
    if (idx !== -1) {
      s = s.slice(0, idx) + s.slice(idx + c.length)
      break
    }
  }
  s = s.replace(/\(\s*\)/g, '')
  s = s.replace(/\(\s*(AND|OR)\s+/gi, '(')
  s = s.replace(/\s+(AND|OR)\s*\)/gi, ')')
  s = s.replace(/^\s*(AND|OR)\s+/i, '')
  s = s.replace(/\s+(AND|OR)\s*$/i, '')
  s = s.replace(/\s{2,}/g, ' ')
  return s.trim()
}
